import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Platform, Pressable, ScrollView, StyleSheet, Text, View, type ViewStyle } from "react-native";
import { type EditingTeamRole, type GetModernizedCourseEditingTeamQuery } from "@axio/graphql/generated/graphql";
import { EyeIcon, XIcon } from "@phosphor-icons/react";
import { GlobalCommentsSheet } from "./components/GlobalCommentsSheet";
import ExpandedReviewContextBar from "./components/ExpandedReviewContextBar";
import PublishSheet from "./components/PublishSheet";
import ReviewActionPanel from "./components/ReviewActionPanel";
import ReviewContentPanel from "./components/ReviewContentPanel";
import ReviewSidebar from "./components/ReviewSidebar";
import ReviewTopBar from "./components/ReviewTopBar";
import VersionHistorySheetContainer from "./components/VersionHistorySheetContainer";
import { useCommentThreads } from "./hooks/useCommentThreads";
import { TeamMembersProvider } from "./contexts/TeamMembersContext";
import type { TeamManagementUserOption } from "./components/TeamManagementPanel";
import { formatSnapshotTime, type SnapshotVersionEntry } from "./components/VersionHistorySheet";
import CustomText from "../../../../components/common/general/CustomText/CustomText";
import type { CourseModernizerReviewPermissions } from "./reviewPermissions";
import { COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS } from "./constants/itemTypeLabels";
import type {
  CourseEvaluatorReviewActivityMove,
  CourseModernizerReviewTeamManagement,
  CourseEvaluatorReviewDecision,
  CourseEvaluatorReviewData,
  CourseEvaluatorReviewSidebarGroup,
  CourseEvaluatorReviewSidebarItem,
} from "./types/reviewTypes";
import type {
  ActivityDetail,
  PublishedPublishItem,
  PublishDocumentUpdate,
  PublishManualStep,
  PublishStatus,
  QueuedPublishItem,
  StructuredFieldEdits,
} from "./types/reviewTypes";
import { colors } from "../../../../styles/design-system";

type EditingTeamResult = GetModernizedCourseEditingTeamQuery["getModernizedCourseEditingTeam"];

function getExpandedItemTypeLabel(item: CourseEvaluatorReviewSidebarItem): string {
  if (item.kind === "moduleDescription") return "Module Description";
  if (item.kind === "recommendation") return "Recommendation";

  const itemTypeMeta = item.meta?.find((metaEntry) => metaEntry.kind === "itemType");
  if (!itemTypeMeta || itemTypeMeta.kind !== "itemType") {
    return item.kind === "suggestedContent" ? "Suggested Content" : "Activity";
  }

  return (
    COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS[itemTypeMeta.itemType] ??
    (item.kind === "suggestedContent" ? "Suggested Content" : "Activity")
  );
}

interface HistoricalSnapshotBannerProps {
  snapshot: SnapshotVersionEntry;
  canRestore: boolean;
  isRestorePending: boolean;
  onBackToCurrent: () => void;
  onRestore: () => void;
}

export function HistoricalSnapshotBanner({
  snapshot,
  canRestore,
  isRestorePending,
  onBackToCurrent,
  onRestore,
}: HistoricalSnapshotBannerProps) {
  const isRestoreDisabled = isRestorePending || !canRestore;

  return (
    <View role="status" style={styles.historicalBanner}>
      <View style={styles.historicalBannerMessage}>
        <EyeIcon size={16} color="#012298" weight="fill" />
        <Text numberOfLines={1} style={styles.historicalBannerText}>
          Viewing <Text style={styles.historicalBannerVersion}>v{snapshot.versionNumber}</Text> from{" "}
          {formatSnapshotTime(snapshot.timestamp)} by {snapshot.authorName} — editing disabled
        </Text>
      </View>
      <View style={styles.historicalBannerActions}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Back to current"
          onPress={onBackToCurrent}
          style={styles.historicalBackButton}>
          <XIcon size={14} color="#344054" weight="regular" />
          <CustomText
            text="Back to current"
            useTranslationText={false}
            size="xs"
            weight="medium"
            style={styles.historicalBackButtonText}
          />
        </Pressable>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Restore this version"
          accessibilityState={{ disabled: isRestoreDisabled }}
          disabled={isRestoreDisabled}
          onPress={onRestore}
          style={[styles.historicalRestoreButton, isRestoreDisabled && styles.historicalRestoreButtonDisabled]}>
          <CustomText
            text={isRestorePending ? "Restoring..." : "Restore this version"}
            useTranslationText={false}
            size="xs"
            weight="medium"
            style={styles.historicalRestoreButtonText}
          />
        </Pressable>
      </View>
    </View>
  );
}

interface RestoreSnapshotConfirmationModalProps {
  snapshot: SnapshotVersionEntry | null;
  visible: boolean;
  isRestorePending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function RestoreSnapshotConfirmationModal({
  snapshot,
  visible,
  isRestorePending,
  onCancel,
  onConfirm,
}: RestoreSnapshotConfirmationModalProps) {
  if (!visible || !snapshot) {
    return null;
  }

  const confirmLabel = isRestorePending ? "Restoring..." : "Save current and restore";

  return (
    <Modal
      role="dialog"
      aria-modal={true}
      accessibilityViewIsModal
      animationType="fade"
      transparent
      visible
      onRequestClose={onCancel}>
      <View style={styles.restoreModalOverlay}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Dismiss restore confirmation"
          disabled={isRestorePending}
          onPress={onCancel}
          style={styles.restoreModalBackdrop}
        />
        <View style={styles.restoreModalCard}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Close restore confirmation"
            disabled={isRestorePending}
            onPress={onCancel}
            style={styles.restoreModalCloseButton}>
            <XIcon size={16} color={colors.grey[500]} weight="regular" />
          </Pressable>
          <CustomText
            text={`Restore v${snapshot.versionNumber}?`}
            useTranslationText={false}
            size="xl"
            weight="bold"
            role="heading"
            aria-level="2"
            style={styles.restoreModalTitle}
          />
          <CustomText
            text={`Your current state will be saved as a new version before restoring v${snapshot.versionNumber}. You can always view it again from history.`}
            useTranslationText={false}
            size="s"
            style={styles.restoreModalDescription}
          />
          <View style={styles.restoreModalActions}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Cancel restore"
              disabled={isRestorePending}
              onPress={onCancel}
              style={[styles.restoreCancelButton, isRestorePending && styles.restoreButtonDisabled]}>
              <CustomText text="Cancel" useTranslationText={false} size="s" style={styles.restoreCancelButtonText} />
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`Confirm restore v${snapshot.versionNumber}`}
              accessibilityState={{ disabled: isRestorePending }}
              disabled={isRestorePending}
              onPress={onConfirm}
              style={[styles.restoreConfirmButton, isRestorePending && styles.restoreButtonDisabled]}>
              <CustomText
                text={confirmLabel}
                useTranslationText={false}
                size="s"
                weight="medium"
                style={styles.restoreConfirmButtonText}
              />
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

interface ReviewPanelProps {
  review: CourseEvaluatorReviewData;
  activityDetails: Record<string, ActivityDetail>;
  permissions: CourseModernizerReviewPermissions;
  editingTeamResult: EditingTeamResult | null;
  currentUserId: string | null;
  teamManagement: CourseModernizerReviewTeamManagement | null;
  teamUserOptions: TeamManagementUserOption[];
  teamSearchQuery: string;
  onTeamSearchQueryChange: (value: string) => void;
  selectedTeamRole: EditingTeamRole;
  onSelectedTeamRoleChange: (role: EditingTeamRole) => void;
  isSavingTeamManagement: boolean;
  onEditingTeamChange: (nextMembers: Array<{ userId: string; role: EditingTeamRole }>) => void;
  selectedItemId: string | null;
  selectedReviewItem: CourseEvaluatorReviewSidebarItem | null;
  selectedDecision: CourseEvaluatorReviewDecision;
  filterMode: "all" | "undecided";
  collapsedSectionIds: Set<string>;
  reviewedProgress: number;
  getDecision: (id: string) => CourseEvaluatorReviewDecision;
  lastSavedAt?: string | null;
  isSaving?: boolean;
  onExitReview: () => void;
  onSelectItem: (itemId: string | null) => void;
  onToggleSection: (sectionId: string) => void;
  onFilterModeChange: (mode: "all" | "undecided") => void;
  onStartReviewing: () => void;
  onAddModule?: () => void;
  onDeleteActivity?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onUndoDeleteActivity?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onDeleteModule?: (group: CourseEvaluatorReviewSidebarGroup) => void;
  onUndoDeleteModule?: (group: CourseEvaluatorReviewSidebarGroup) => void;
  onAddActivity?: (group: CourseEvaluatorReviewSidebarGroup) => void;
  onAccept?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onReject?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onAcceptGroup?: (group: CourseEvaluatorReviewSidebarGroup) => void;
  onRejectGroup?: (group: CourseEvaluatorReviewSidebarGroup) => void;
  onModuleDragEnd?: (groups: CourseEvaluatorReviewSidebarGroup[]) => void | Promise<void>;
  onActivityDragEnd?: (
    group: CourseEvaluatorReviewSidebarGroup,
    items: CourseEvaluatorReviewSidebarItem[]
  ) => void | Promise<void>;
  onActivityMoveEnd?: (move: CourseEvaluatorReviewActivityMove) => void | Promise<void>;
  onUndo?: () => void;
  onSaveEdit?: (item: CourseEvaluatorReviewSidebarItem, editedFields: StructuredFieldEdits) => void;
  onCreateIle?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onPreviewIle?: (item: CourseEvaluatorReviewSidebarItem) => void;
  isGeneratingIle?: boolean;
  onNavigateNext?: () => void;
  onNavigatePrev?: () => void;
  onJumpToEntity?: (entityId: string, dimension: string) => void;
  // Publish flow
  queuedPublishItems: QueuedPublishItem[];
  lastPublishedItems: PublishedPublishItem[];
  publishManualSteps: PublishManualStep[];
  publishDocumentUpdates: PublishDocumentUpdate[];
  undecidedCount: number;
  isPublishSheetOpen: boolean;
  publishStatus: PublishStatus;
  onOpenPublishSheet: () => void;
  onClosePublishSheet: () => void;
  onDonePublish: () => void;
  onPublish: () => void;
  onExportPdf: () => void;
  onExportImscc: () => void;
  isExportingPdf?: boolean;
  isExportingImscc?: boolean;
  isVersionHistoryOpen: boolean;
  onOpenVersionHistory: () => void;
  onCloseVersionHistory: () => void;
  viewingSnapshot: SnapshotVersionEntry | null;
  isRestoreSnapshotModalOpen: boolean;
  isRestoreSnapshotPending?: boolean;
  onViewSnapshot: (snapshot: SnapshotVersionEntry) => void;
  onBackToCurrentSnapshot: () => void;
  onOpenRestoreSnapshotConfirmation: () => void;
  onCancelRestoreSnapshot: () => void;
  onConfirmRestoreSnapshot: () => void;
  canAcceptSelectedItem?: boolean;
  acceptDisabledReason?: string;
  acceptHiddenReason?: string;
}

function ReviewPanel({
  review,
  activityDetails,
  permissions,
  editingTeamResult,
  currentUserId,
  teamManagement,
  teamUserOptions,
  teamSearchQuery,
  onTeamSearchQueryChange,
  selectedTeamRole,
  onSelectedTeamRoleChange,
  isSavingTeamManagement,
  onEditingTeamChange,
  selectedItemId,
  selectedReviewItem,
  selectedDecision,
  filterMode,
  collapsedSectionIds,
  reviewedProgress,
  getDecision,
  lastSavedAt,
  isSaving,
  onExitReview,
  onSelectItem,
  onToggleSection,
  onFilterModeChange,
  onStartReviewing,
  onAddModule,
  onDeleteActivity,
  onUndoDeleteActivity,
  onDeleteModule,
  onUndoDeleteModule,
  onAddActivity,
  onAccept,
  onReject,
  onAcceptGroup,
  onRejectGroup,
  onModuleDragEnd,
  onActivityDragEnd,
  onActivityMoveEnd,
  onUndo,
  onSaveEdit,
  onCreateIle,
  onPreviewIle,
  isGeneratingIle = false,
  onNavigateNext,
  onNavigatePrev,
  onJumpToEntity,
  queuedPublishItems,
  lastPublishedItems,
  publishManualSteps,
  publishDocumentUpdates,
  undecidedCount,
  isPublishSheetOpen,
  publishStatus,
  onOpenPublishSheet,
  onClosePublishSheet,
  onDonePublish,
  onPublish,
  onExportPdf,
  onExportImscc,
  isExportingPdf = false,
  isExportingImscc = false,
  isVersionHistoryOpen,
  onOpenVersionHistory,
  onCloseVersionHistory,
  viewingSnapshot,
  isRestoreSnapshotModalOpen,
  isRestoreSnapshotPending = false,
  onViewSnapshot,
  onBackToCurrentSnapshot,
  onOpenRestoreSnapshotConfirmation,
  onCancelRestoreSnapshot,
  onConfirmRestoreSnapshot,
  canAcceptSelectedItem,
  acceptDisabledReason,
  acceptHiddenReason,
}: ReviewPanelProps) {
  const isSelectedItemPublished = selectedDecision === "synced";
  const isViewingHistoricalSnapshot = viewingSnapshot !== null;
  const isWriteDisabled = isSelectedItemPublished || isViewingHistoricalSnapshot || !permissions.canEdit;
  const isCommentDisabled = isSelectedItemPublished || isViewingHistoricalSnapshot || !permissions.canComment;
  const [isOverviewOverlayVisible, setIsOverviewOverlayVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const canMoveActivitiesAcrossModules = review.canMoveActivitiesAcrossModules !== false;

  const handleToggleExpand = useCallback(() => {
    setIsExpanded((previous) => !previous);
  }, []);

  useEffect(() => {
    if (!selectedReviewItem) {
      setIsExpanded(false);
    }
  }, [selectedReviewItem]);

  const orderedItems = useMemo(() => review.sidebarGroups.flatMap((group) => group.items), [review.sidebarGroups]);

  const selectedItemPosition = useMemo(() => {
    if (!selectedReviewItem) return -1;
    return orderedItems.findIndex((item) => item.id === selectedReviewItem.id);
  }, [orderedItems, selectedReviewItem]);

  const selectedActivityDetail = selectedReviewItem ? activityDetails[selectedReviewItem.id] : null;
  const [isCommentsSheetOpen, setIsCommentsSheetOpen] = useState(false);
  const [commentPinOpenForItemId, setCommentPinOpenForItemId] = useState<string | null>(null);

  // Collect all entity IDs from the review to query threads for
  const allEntityIds = useMemo(
    () => review.sidebarGroups.flatMap((group) => group.items.map((item) => item.id)),
    [review.sidebarGroups]
  );

  const allEntityIdSet = useMemo(() => new Set(allEntityIds), [allEntityIds]);

  const { commentsApi, threads, refetch: refetchThreads } = useCommentThreads(allEntityIds, review.courseId);

  const prevPublishStatusRef = useRef(publishStatus);
  useEffect(() => {
    const wasPublishing = prevPublishStatusRef.current === "publishing";
    const donePublishing = publishStatus === "done" || publishStatus === "error";
    if (wasPublishing && donePublishing) {
      refetchThreads();
    }
    prevPublishStatusRef.current = publishStatus;
  }, [publishStatus, refetchThreads]);

  const commentedItemIds = useMemo(
    () => new Set(threads.filter((t) => t.root.targetId).map((t) => t.root.targetId!)),
    [threads]
  );

  const commentedItemCounts = useMemo(() => {
    const counts = new Map<string, number>();
    threads.forEach((t) => {
      if (!t.root.targetId) return;
      counts.set(t.root.targetId, (counts.get(t.root.targetId) ?? 0) + 1);
    });
    return counts;
  }, [threads]);

  const unresolvedItemCounts = useMemo(() => {
    const counts = new Map<string, number>();
    threads.forEach((t) => {
      if (!t.root.targetId || t.root.resolved) return;
      counts.set(t.root.targetId, (counts.get(t.root.targetId) ?? 0) + 1);
    });
    return counts;
  }, [threads]);

  // Keyboard navigation: ArrowLeft/ArrowRight to switch between items
  useEffect(() => {
    if (Platform.OS !== "web") return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input/textarea
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement)?.isContentEditable) return;

      if (e.key === "ArrowRight") {
        e.preventDefault();
        onNavigateNext?.();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        onNavigatePrev?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onNavigateNext, onNavigatePrev]);

  return (
    <TeamMembersProvider courseId={review.courseId} editingTeamResult={editingTeamResult}>
      <View style={styles.page}>
        {!isExpanded && (
          <ReviewTopBar
            review={review}
            permissions={permissions}
            reviewedProgress={reviewedProgress}
            lastSavedAt={lastSavedAt}
            isSaving={isSaving}
            commentsCount={threads.length}
            hasCommentsIndicator={
              permissions.isArchitect
                ? threads.some(
                    (thread) =>
                      !thread.root.resolved && !(thread.deleted && !allEntityIdSet.has(thread.root.targetId ?? ""))
                  )
                : threads.some(
                    (thread) =>
                      !thread.root.resolved &&
                      !(thread.deleted && !allEntityIdSet.has(thread.root.targetId ?? "")) &&
                      thread.root.assigneeId === currentUserId
                  )
            }
            onExitReview={onExitReview}
            onOpenPublishSheet={onOpenPublishSheet}
            onOpenVersionHistory={onOpenVersionHistory}
            onOpenCommentsSheet={() => setIsCommentsSheetOpen(true)}
            isHistoricalSnapshotView={isViewingHistoricalSnapshot}
          />
        )}

        {!isExpanded && viewingSnapshot && (
          <HistoricalSnapshotBanner
            snapshot={viewingSnapshot}
            canRestore={permissions.canRestoreVersion}
            isRestorePending={isRestoreSnapshotPending}
            onBackToCurrent={onBackToCurrentSnapshot}
            onRestore={permissions.canRestoreVersion ? onOpenRestoreSnapshotConfirmation : () => undefined}
          />
        )}

        <View style={styles.workspace}>
          {!isExpanded && (
            <ReviewSidebar
              review={review}
              selectedItemId={selectedItemId}
              filterMode={filterMode}
              collapsedSectionIds={collapsedSectionIds}
              commentedItemIds={commentedItemIds}
              commentedItemCounts={commentedItemCounts}
              unresolvedItemCounts={unresolvedItemCounts}
              getDecision={getDecision}
              onSelectItem={onSelectItem}
              onToggleSection={onToggleSection}
              onFilterModeChange={onFilterModeChange}
              onAddModule={isViewingHistoricalSnapshot ? undefined : onAddModule}
              onDeleteActivity={isViewingHistoricalSnapshot ? undefined : onDeleteActivity}
              onUndoDeleteActivity={isViewingHistoricalSnapshot ? undefined : onUndoDeleteActivity}
              onDeleteModule={isViewingHistoricalSnapshot ? undefined : onDeleteModule}
              onUndoDeleteModule={isViewingHistoricalSnapshot ? undefined : onUndoDeleteModule}
              onAddActivity={isViewingHistoricalSnapshot ? undefined : onAddActivity}
              onAcceptGroup={isViewingHistoricalSnapshot ? undefined : onAcceptGroup}
              onRejectGroup={isViewingHistoricalSnapshot ? undefined : onRejectGroup}
              onModuleDragEnd={isViewingHistoricalSnapshot ? undefined : onModuleDragEnd}
              onActivityDragEnd={isViewingHistoricalSnapshot ? undefined : onActivityDragEnd}
              onActivityMoveEnd={
                isViewingHistoricalSnapshot || !canMoveActivitiesAcrossModules ? undefined : onActivityMoveEnd
              }
              permissions={permissions}
            />
          )}

          <View style={styles.contentColumn}>
            {isExpanded && selectedReviewItem && (
              <ExpandedReviewContextBar
                itemTypeLabel={getExpandedItemTypeLabel(selectedReviewItem)}
                courseName={review.courseName}
                moduleActivityThread={selectedActivityDetail?.documentReference ?? selectedReviewItem.title}
                activityTitle={selectedReviewItem.title}
                positionIndex={selectedItemPosition + 1}
                totalCount={orderedItems.length}
                whyFindings={selectedActivityDetail?.issueFindingsDetailed}
                whyRubricCode={selectedActivityDetail?.rubricCode}
                whyRubricTitle={selectedActivityDetail?.rubricTitle}
                whyRubricDescription={selectedActivityDetail?.rubricDescription}
                whyLearningObjective={selectedActivityDetail?.learningObjective}
                onCollapse={() => setIsExpanded(false)}
              />
            )}

            <ReviewActionPanel
              selectedReviewItem={selectedReviewItem}
              decision={selectedDecision}
              onAccept={isViewingHistoricalSnapshot ? undefined : onAccept}
              onReject={isViewingHistoricalSnapshot ? undefined : onReject}
              onUndo={isViewingHistoricalSnapshot ? undefined : onUndo}
              permissions={permissions}
              onNavigateNext={onNavigateNext}
              onNavigatePrev={onNavigatePrev}
              canAcceptSelectedItem={isViewingHistoricalSnapshot ? false : canAcceptSelectedItem}
              acceptDisabledReason={
                isViewingHistoricalSnapshot
                  ? "Editing is disabled while viewing a historical version."
                  : acceptDisabledReason
              }
              isExpanded={isExpanded}
              onToggleExpand={handleToggleExpand}
              acceptHiddenReason={acceptHiddenReason}
              commentsApi={commentsApi}
              commentTargetKind={selectedReviewItem?.entityType ?? "assignment"}
              commentTargetId={selectedReviewItem?.id ?? null}
              commentsDisabled={isCommentDisabled}
              commentPinOpen={commentPinOpenForItemId === (selectedReviewItem?.id ?? null)}
              onCommentPinOpenChange={(isOpen) => {
                if (isOpen) {
                  setCommentPinOpenForItemId(selectedReviewItem?.id ?? null);
                } else {
                  setCommentPinOpenForItemId(null);
                }
              }}
            />

            <ScrollView
              style={styles.contentScroll}
              contentContainerStyle={[styles.contentScrollContent, isExpanded && styles.contentScrollContentExpanded]}
              scrollEnabled={!isOverviewOverlayVisible}
              tabIndex={0}
              showsVerticalScrollIndicator>
              <View style={[styles.contentInner, isExpanded && styles.contentInnerExpanded]}>
                <ReviewContentPanel
                  review={review}
                  selectedReviewItem={selectedReviewItem}
                  activityDetails={activityDetails}
                  isArchitect={!isViewingHistoricalSnapshot && permissions.canManageTeam}
                  currentUserId={currentUserId}
                  teamManagement={!isViewingHistoricalSnapshot && permissions.canManageTeam ? teamManagement : null}
                  teamUserOptions={teamUserOptions}
                  teamSearchQuery={teamSearchQuery}
                  onTeamSearchQueryChange={onTeamSearchQueryChange}
                  selectedTeamRole={selectedTeamRole}
                  onSelectedTeamRoleChange={onSelectedTeamRoleChange}
                  isSavingTeamManagement={isSavingTeamManagement}
                  onEditingTeamChange={onEditingTeamChange}
                  publishManualSteps={publishManualSteps}
                  isExpanded={isExpanded}
                  isEditDisabled={isWriteDisabled}
                  onStartReviewing={onStartReviewing}
                  onOverlayVisibilityChange={setIsOverviewOverlayVisible}
                  onSaveEdit={!isViewingHistoricalSnapshot && permissions.canEdit ? onSaveEdit : undefined}
                  onCreateIle={!isViewingHistoricalSnapshot && permissions.canEdit ? onCreateIle : undefined}
                  onPreviewIle={isViewingHistoricalSnapshot ? undefined : onPreviewIle}
                  isGeneratingIle={isGeneratingIle}
                  onAccept={!isViewingHistoricalSnapshot && permissions.canQueue ? onAccept : undefined}
                  onReject={!isViewingHistoricalSnapshot && permissions.canQueue ? onReject : undefined}
                  onAcceptWithoutNav={!isViewingHistoricalSnapshot && permissions.canQueue ? onAccept : undefined}
                  onRejectWithoutNav={!isViewingHistoricalSnapshot && permissions.canQueue ? onReject : undefined}
                  onJumpToEntity={onJumpToEntity}
                />
              </View>
            </ScrollView>
          </View>
        </View>

        <PublishSheet
          open={permissions.canPublish && !isViewingHistoricalSnapshot && isPublishSheetOpen}
          items={queuedPublishItems}
          publishedItems={lastPublishedItems}
          manualSteps={publishManualSteps}
          documentUpdates={publishDocumentUpdates}
          undecidedCount={undecidedCount}
          lmsLabel={review.sourceLabel}
          canPublishToLms={review.canPublishToLms}
          status={publishStatus}
          onClose={onClosePublishSheet}
          onDone={onDonePublish}
          onPublish={permissions.canPublish ? onPublish : () => undefined}
          onExportPdf={permissions.canPublish ? onExportPdf : () => undefined}
          onExportImscc={permissions.canPublish ? onExportImscc : () => undefined}
          isExportingPdf={isExportingPdf}
          isExportingImscc={isExportingImscc}
        />
        <VersionHistorySheetContainer
          open={isVersionHistoryOpen}
          courseId={review.courseId}
          courseName={review.courseName}
          onClose={onCloseVersionHistory}
          onViewSnapshot={onViewSnapshot}
        />
        <RestoreSnapshotConfirmationModal
          snapshot={viewingSnapshot}
          visible={permissions.canRestoreVersion && isRestoreSnapshotModalOpen}
          isRestorePending={isRestoreSnapshotPending}
          onCancel={onCancelRestoreSnapshot}
          onConfirm={onConfirmRestoreSnapshot}
        />
        <GlobalCommentsSheet
          open={isCommentsSheetOpen}
          onClose={() => setIsCommentsSheetOpen(false)}
          api={commentsApi}
          items={review.sidebarGroups.flatMap((group) => group.items)}
          canResolveAll={permissions.canComment}
          onJumpToThread={(_targetKind, targetId) => {
            onSelectItem(targetId);
            setCommentPinOpenForItemId(targetId);
          }}
        />
      </View>
    </TeamMembersProvider>
  );
}

const styles = StyleSheet.create({
  historicalBanner: {
    height: 47,
    backgroundColor: "#E6EBFF",
    paddingHorizontal: 24,
    paddingVertical: 10,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    flexShrink: 0,
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
  },
  historicalBannerMessage: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    flex: 1,
    minWidth: 0,
  },
  historicalBannerText: {
    color: "#182230",
    fontFamily: "Inter",
    fontSize: 14,
    fontWeight: "400",
    lineHeight: 21,
    flexShrink: 1,
    minWidth: 0,
  },
  historicalBannerVersion: {
    fontWeight: "600",
  },
  historicalBannerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  historicalBackButton: {
    height: 26,
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "row",
    gap: 6,
    backgroundColor: "transparent",
  },
  historicalBackButtonText: {
    color: "#344054",
    lineHeight: 18,
  },
  historicalRestoreButton: {
    height: 26,
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
    justifyContent: "center",
    backgroundColor: "#3E68FE",
    ...(Platform.OS === "web"
      ? ({
          boxShadow: "0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06)",
        } as unknown as ViewStyle)
      : {
          shadowColor: "#101828",
          shadowOpacity: 0.1,
          shadowRadius: 3,
          shadowOffset: { width: 0, height: 1 },
          elevation: 1,
        }),
  },
  historicalRestoreButtonDisabled: {
    opacity: 0.6,
  },
  historicalRestoreButtonText: {
    color: colors.white,
    lineHeight: 18,
  },
  restoreModalOverlay: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(16, 24, 40, 0.42)",
    paddingHorizontal: 20,
  },
  restoreModalBackdrop: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  restoreModalCard: {
    width: "100%",
    maxWidth: 448,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
    padding: 24,
    gap: 16,
    ...(Platform.OS === "web"
      ? ({
          boxShadow: "0 12px 16px -4px rgba(16, 24, 40, 0.08), 0 4px 6px -2px rgba(16, 24, 40, 0.03)",
        } as unknown as ViewStyle)
      : {
          shadowColor: "#101828",
          shadowOpacity: 0.08,
          shadowRadius: 16,
          shadowOffset: { width: 0, height: 12 },
          elevation: 8,
        }),
  },
  restoreModalCloseButton: {
    position: "absolute",
    top: 14,
    right: 14,
    width: 28,
    height: 28,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  restoreModalTitle: {
    color: colors.grey[700],
    fontSize: 20,
    lineHeight: 20,
  },
  restoreModalDescription: {
    color: colors.grey[700],
    lineHeight: 21,
    marginTop: -10,
    paddingRight: 18,
  },
  restoreModalActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 8,
  },
  restoreCancelButton: {
    minHeight: 33,
    justifyContent: "center",
    borderRadius: 6,
    backgroundColor: "transparent",
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  restoreCancelButtonText: {
    color: colors.grey[700],
    lineHeight: 21,
  },
  restoreConfirmButton: {
    minHeight: 33,
    justifyContent: "center",
    borderRadius: 6,
    backgroundColor: "#3E68FE",
    paddingHorizontal: 12,
    paddingVertical: 6,
    ...(Platform.OS === "web"
      ? ({
          boxShadow: "0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06)",
        } as unknown as ViewStyle)
      : {
          shadowColor: "#101828",
          shadowOpacity: 0.1,
          shadowRadius: 3,
          shadowOffset: { width: 0, height: 1 },
          elevation: 1,
        }),
  },
  restoreButtonDisabled: {
    opacity: 0.55,
  },
  restoreConfirmButtonText: {
    color: colors.white,
    lineHeight: 21,
  },
  page: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    ...(Platform.OS === "web"
      ? ({
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          height: "100vh",
          overflow: "hidden",
          display: "flex",
        } as unknown as ViewStyle)
      : {}),
  },
  workspace: {
    flex: 1,
    flexDirection: "row",
    backgroundColor: colors.grey[50],
    minHeight: 0,
    ...(Platform.OS === "web"
      ? ({
          display: "flex",
          overflow: "hidden",
        } as const)
      : {}),
  },
  contentColumn: {
    flex: 1,
    minWidth: 0,
    backgroundColor: colors.grey[50],
  },
  contentScroll: {
    flex: 1,
    minHeight: 0,
    overflow: "scroll",
  },
  contentScrollContent: {
    maxWidth: 1024,
    alignSelf: "center",
    width: "100%",
    padding: 24,
    flexGrow: 1,
  },
  contentScrollContentExpanded: {
    maxWidth: "100%",
    alignSelf: "stretch",
    padding: 0,
  },
  contentInner: {
    width: "100%",
    gap: 16,
  },
  contentInnerExpanded: {
    flex: 1,
    minHeight: 0,
  },
});

export default ReviewPanel;
